<template>
    <div id="container" class="flex-container">
        <div id="main-panel">
            <div id="display">
                <canvas id="screen" ref="screen" tabindex="1" :class="dnd?'dnd':''"></canvas>
            </div>

            <div class="flex-container flex-space-between">

                <div id="clk-panel" v-if="machine">
                    <button
                            @click="clk">Clk</button>
                    <button 
                            @click="toggleAutoclk" 
                            :class="autoclk.interval ? 'on':'off'">Autoclk</button>
                    
                    <button
                            v-for="speed in autoclk.presetSpeeds"
                            :key="speed.freq"
                            :class="autoclk.freq == speed.freq ? 'on' : 'off'"
                            @click="changeAutoclkFreq(speed.freq)"
                            >{{ speed.caption }}</button>

                </div>

                <div id="ctl-panel" v-if="machine">
                    <button @click="dnd=!dnd" :class="dnd ? 'on' : 'off'">DND</button>
                    <button @click="reset">Reset</button>
                    <button @click="nmi">NMI</button>
                </div>
            </div>
            
            <div id="mon" v-if="mon.state && !dnd">
                <div class="flex-container flex-space-between">
                    <div>
                        &nbsp;PC=${{ mon.state.pc | hex16 }} &nbsp;({{ mon.state.symbolicPC.symbolName }}+{{ mon.state.symbolicPC.rel }})
                    </div>
                    <div>
                        CLK={{ mon.state.clk }}
                        INTCLK={{ mon.state.intclk }} ({{ ((mon.state.intclk / mon.state.clk)*100).toFixed(2) }}%)
                    </div>
                </div>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                NV-BDIZC
                <br/>
                &nbsp;SP=${{ mon.state.sp | hex8 }}
                &nbsp;&nbsp;&nbsp;
                
                P=%{{ mon.state.p | bin8 }}=${{ mon.state.p | hex8 }}
                &nbsp;&nbsp;&nbsp;
                A=${{ mon.state.a | hex8 }}
                X=${{ mon.state.x | hex8 }}
                Y=${{ mon.state.y | hex8 }}

                
                <br/>
                <span v-for="(r, n) in mon.state.rs" :key="n">
                    <span v-if="n<10">&nbsp;</span>r{{n}}=${{ r | hex16 }}
                    <br v-if="n == 7"/>
                </span>
                <br/>
                <div class="add-watch">
                    <input type="text" v-model="addWatch.ref" @keydown.13="addWatchBtn" />
                    <input type="text" v-model="addWatch.size" @keydown.13="addWatchBtn" />
                    <button @click="addWatchBtn">Add</button>
                </div>
                <div class="watches">
                    <div class="watch" v-for="(w, idx) in mon.state.watches" :key="w.watch.address">
                        <div class="header">
                            <div class="flex-container flex-space-between">
                                <div>
                                    m <span v-if="w.watch.symbol">{{ w.watch.symbol }}</span>
                                    ${{ w.watch.address | hex16 }}
                                </div>
                                <div>
                                    <button v-if="idx != mon.state.watches.length-1" @click="moveDownWatch(w.watch)">&#9660;</button>
                                    <button v-if="idx != 0" @click="moveUpWatch(w.watch)">&#9650;</button>
                                    <button @click="removeWatch(w.watch)">X</button>                        
                                </div>
                            </div>
                        </div>
                        <div class="dump">{{ w.dump }}</div>
                    </div>
                </div>
            </div>
        </div>

        <div id="side-panel" v-if="!dnd">
            <div id="fds" v-if="mon.state">
                <div>FD list</div>
                <div v-for="(fd, idx) in mon.state.fdsDump" :key="fd.fileno">
                    {{ fd.fileno }}:
                    <span v-if="fd.flags != 0">
                        {{ fd.pathname }}<br/>
                        flags=${{ fd.flagsHex }}
                        pos=${{ fd.posHex }} ({{ fd.pos }})
                        devno={{ fd.devno }}
                    </span>
                    <span v-else>
                        closed
                    </span>
                </div>
            </div>

            <table id="symbols" v-if="false">
                <tr v-for="item in symbols.alphabeticList" :key="item.tag">
                    <td>{{ item.symbol }}</td>
                    <td>{{ item.address | hex16}}</td>
                </tr>
            </table>
        </div>
    </div>
</template>

<script>
import Machine from "@/emu/machine";
import Symbols from "@/emu/symbols";

export default {   

    data() {
        return {
            rcs: {
                bolmos: {
                    url: "static/bolmos.img"
                },
                spikey: {
                    url: "static/spikey.img"
                },
                spikeylbl: {
                    url: "static/spikey.lbl"
                },
                rombank: {
                    url: "static/rombank.img"
                },
                bolmoslbl: {
                    url: "static/bolmos.lbl"
                }
            },

            machine: null,
            autoclk: {
                schedule: 10, // msec
                freq: 100000,
                interval: null,
                presetSpeeds: [{
                    freq: 10000,
                    caption: '10 KHz'
                }, {
                    freq: 100000,
                    caption: '100 KHz'
                }, {
                    freq: 1000000,
                    caption: '1 MHz'
                }, {
                    freq: 10000000,
                    caption: '10 MHz'
                }],
                forceStop: false
            },

            adaFrameHandle: null,
            mon: {
                schedule: 50, // msec
                interval: null,
                watches: [],
                state: null
            },

            addWatch: this.buildEmptyAddWatch(),

            dnd: false,

            symbols: Symbols()
        };
    },

    mounted() {
        this.fetchResources()
            .then(this.buildEmulator)
            .then(this.startAutoclk);
    },

    beforeDestroy() {
        if (this.adaFrameHandle) {
            window.cancelAnimationFrame(this.adaFrameHandle);
        }
        if (this.mon.interval) {
            clearInterval(this.mon.interval);
        }

        this.stopAutoclk();
    },

    methods: {
        removeWatch(w) {
            this.mon.watches.splice(this.mon.watches.indexOf(w), 1);
            this.saveDebuggerState();
        },

        loadDebuggerState() {
            const savedData = window.localStorage.getItem("debuggerState");
            if (savedData) {
                try {
                    const o = JSON.parse(savedData);
                    o.watches.forEach((w) => this.mon.watches.push(w))
                } catch (e) {
                    window.localStorage.removeItem("debuggerState");
                }
            }

            // resolve symbols
            for (let w of this.mon.watches) {
                if (typeof w.address !== "undefined") {
                    continue;
                }

                w.address = this.symbols.getAddress(w.symbol);
                if (w.address == null) {
                    w.address = 0; // FIXME
                }
            }
        },

        saveDebuggerState() {
            const s = {
                watches: []
            };

            s.watches.push(...this.mon.watches.map(w => {
                const o = { size: w.size };
                if (w.symbol) {
                    o.symbol = w.symbol;
                } else {
                    o.address = w.address;
                }
                return o;
            }));

            window.localStorage.setItem("debuggerState", JSON.stringify(s));
        },

        buildEmptyAddWatch() {
            return {
                ref: "",
                size: 64
            };
        },

        addWatchBtn() {
            const a = this.addWatch;

            let addr = this.symbols.getAddress(a.ref);
            let symbol;
            if (addr == null) {
                addr = parseInt(a.ref, 16);
                if (isNaN(addr)) {
                    return;
                }
            } else {
                symbol = a.ref;
            }

            this.mon.watches.push({
                address: addr,
                size: parseInt(a.size),
                symbol: symbol
            });
            this.addWatch = this.buildEmptyAddWatch();
            this.saveDebuggerState();
            this.$refs.screen.focus();
        },

        moveUpWatch(w) {
            const ws = this.mon.watches;
            const wi = ws.indexOf(w);
            if (wi < 1) {
                return;
            }
            const wo = ws[wi];
            ws[wi] = ws[wi-1];
            ws[wi-1] = wo;
            this.saveDebuggerState();
        },

        moveDownWatch(w) {
            const ws = this.mon.watches;
            const wi = ws.indexOf(w);
            if (wi >= ws.length-1) {
                return;
            }
            const wo = ws[wi];
            ws[wi] = ws[wi+1];
            ws[wi+1] = wo;
            this.saveDebuggerState();
        },

        fetchResources() {
            return Promise.all(Object.keys(this.rcs).map(key => fetch(this.rcs[key].url)))
                .then(resps => Promise.all(resps.map(resp => resp.arrayBuffer())))
                .then(bufs => {
                    const rcKeys = Object.keys(this.rcs);
                    for (let i = 0; i < bufs.length; i++) {
                        this.rcs[rcKeys[i]].data = Buffer.from(bufs[i]);
                    }
                });
        },

        invalidInstruction() {
            console.log("invalid instruction trap");
            this.stopAutoclk();
            this.autoclk.forceStop = true;
        },

        buildEmulator() {
            const machine = Machine({
                romData: this.rcs.bolmos.data,
                romBank: this.rcs.rombank.data,
                invalidInstructionCallback: this.invalidInstruction
            });
            this.rcs.spikey.data.copy(machine.ram.buf, 0x4000);
            this.machine = machine;
            window.machine = this.machine;
            this.adaFrameHandle = window.requestAnimationFrame(this.adaFrame);
            const screen = document.querySelector("#screen");
            screen.width = this.machine.ada.bitmapWidth;
            screen.height = this.machine.ada.bitmapHeight;
            this.mon.interval = setInterval(this.refreshMonState, this.mon.schedule);
            
            screen.addEventListener("keypress", (e) => {
                this.machine.keyboard.feed(e.charCode);
            })

            screen.addEventListener("keydown", (e) => {
                if (e.isComposing) {
                    return;
                }

                switch (e.code) {
                    case "Backspace":       this.machine.keyboard.feed(8); break;
                    case "Escape":          this.machine.keyboard.feed(27); break;
                }

                if (e.keyCode === 32) {
                    this.machine.keyboard.feed(e.keyCode);
                    e.preventDefault();
                }
            });

            window.addEventListener("keydown", (e) => {
                if (e.keyCode === 32 && e.target === document.body) {
                    e.preventDefault();
                }
            });

            this.symbols.load(this.rcs.bolmoslbl.data.toString());
            this.symbols.load(this.rcs.spikeylbl.data.toString());
            this.loadDebuggerState();
            return new Promise((res, rej) => { res(); });
        },

        refreshMonState() {
            if (this.dnd) {
                return;
            }
            const rs = [];
            for (let i = 0; i < 16; i++) {
                const loIdx = i*2;
                const hiIdx = loIdx+1;
                const loByte = this.machine.bus.read(loIdx);
                const hiByte = this.machine.bus.read(hiIdx);
                rs.push((loByte | (hiByte << 8)) >>> 0);
            }

            const watchDumps = [];
            for (let watch of this.mon.watches) {
                const dump = this.machine.dump(watch.address, watch.size);
                watchDumps.push({
                    watch: watch,
                    dump: dump
                });
            }

            const symbolicPC = this.symbols.getClosestLeftSymbol(this.machine.cpu.state.p);
            
            const fdsb = [];
            const fdsAddr = this.symbols.getAddress("__fds");
            const ramBuf = this.machine.ram.buf;
            const getCString = (buf, sliceStart, sliceEnd) => {
                const b = buf.slice(sliceStart, sliceEnd);
                const zeroTerm = b.indexOf(0);
                if (zeroTerm > -1) {
                    return b.slice(0, zeroTerm).toString();
                } else {
                    return b.toString();
                }
            };
            for (let i = 0; i < 8; i++) {
                const base = fdsAddr + (i*112);
                const flags = ramBuf.readUInt8(base);
                const pos = ramBuf.readUInt32LE(base+1);
                const devno = ramBuf.readUInt8(base+5);
                const flagsHex = flags.toString(16).padStart(2, "0");
                const posHex = pos.toString(16).padStart(8, "0");
                const devName = getCString(ramBuf, base+6, base+6+10+1);
                const devPath = getCString(ramBuf, base+6+10+1, base+6+10+1+22);
                const pathname = "/" + devName + (devPath != "/" ? devPath : "");
                fdsb.push({
                    fileno: i,
                    flags: flags,
                    flagsHex: flagsHex,
                    pos: pos,
                    posHex: posHex,
                    devno: devno,
                    pathname: pathname
                });
            }

            const s = {
                pc: this.machine.cpu.state.p,
                sp: this.machine.cpu.state.s,
                a: this.machine.cpu.state.a,
                x: this.machine.cpu.state.x,
                y: this.machine.cpu.state.y,
                p: this.machine.cpu.state.flags,
                clk: this.machine.getElapsedClocks(),
                intclk: this.machine.getIntClocks(),
                rs: rs,
                watches: watchDumps,
                symbolicPC: symbolicPC,
                fdsDump: fdsb
            };
            
            this.mon.state = s;
        },

        adaFrame() {
            const image = machine.ada.getImage();
            if (image.dirty) {
                const ctx = this.$refs.screen.getContext("2d");
                ctx.putImageData(image.imageData, 0, 0);
            }
               
            this.adaFrameHandle = window.requestAnimationFrame(this.adaFrame);                
        },

        startAutoclk() {
            if (this.autoclk.interval) {
                return;
            }

            this.autoclk.interval = setInterval(
                this.autoclkJob, 
                this.autoclk.schedule);

            this.$refs.screen.focus();
        },

        stopAutoclk() {
            if (this.autoclk.interval) {
                clearInterval(this.autoclk.interval);
                this.autoclk.interval = null;
            }
        },

        toggleAutoclk() {
            if (this.autoclk.interval) {
                this.stopAutoclk();
            } else {
                this.startAutoclk();
            }
        },

        autoclkJob() {
            const burstSize = this.autoclk.freq / (1000 / this.autoclk.schedule);
            for (let i = 0; i < burstSize; i++) {
                if (!this.autoclk.forceStop) {
                    this.machine.clk();
                } else {
                    this.autoclk.forceStop = false;
                    break;
                }
            }
        },

        changeAutoclkFreq(freq) {
            this.autoclk.freq = freq;
            this.$refs.screen.focus();
        },

        clk() {
            this.machine.clk();
        },

        reset() {
            this.machine.reset();
            this.$refs.screen.focus();
        },

        nmi() {
            this.machine.nmi();
            this.$refs.screen.focus();
        }
    }
}
</script>

<style scoped>
    #display {
        background: #000;
        padding: 1em;
        display: inline-block;
    }

    #container {
        font-family: monospace, 'Courier New';
        font-size: 11.5pt;
    }

    #screen {
        width: 720px;
        height: 400px;
    }

    .off {
        background: rgb(160, 20, 20);
    }

    .on {
        background: rgb(20, 160, 20);
    }

    .watches .dump {
        white-space: pre;
    }

    canvas:focus {
        outline: none;
    }

    .watches .header,
    .watches .header div,
    .watches .header span {
        background: #000;
    }

    .watches .header {
        padding: 0.25em 0.5em;
    }

    .watch .dump {
        padding: 0.25em 0.5em;
    }

    .flex-container {
        display: flex;
        align-items: center;
    }

    .flex-space-between {
        justify-content: space-between;
    }

    #container {
        align-items: flex-start;
    }

    button {
        color: white;
        padding: 0.5em 1em;
        font-family: inherit;
    }

    input {
        font-family: inherit;
        padding: 0.5em;
        line-height: 1.2em;
    }

    #main-panel {
    }

    #side-panel {
        padding-left: 1em;
    }

    #screen.dnd {
    }


</style>


// WEBPACK FOOTER //
// src/components/Emulator.vue